import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Info, Download, RefreshCw, FileText, Save,
  Copy, ShieldCheck, Sparkles, Calculator, MapPin, FileDown, Printer, ExternalLink, Send, X,
} from "lucide-react";
import WorkforceOsLayout from "../../../components/workforce_os/WorkforceOsLayout";
import { API } from "../../../lib/api";

/* ===================================================================== */
/*  Helpers                                                              */
/* ===================================================================== */

const fmt = (n) => {
  if (!isFinite(n)) return "N/A";
  return Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
const fmtPct = (n) => (isFinite(n) ? `${(n * 100).toFixed(1)}%` : "N/A");
const num = (v) => (v === "" || v === null || v === undefined ? 0 : (parseFloat(v) || 0));

const SAMPLE_DATA = {
  candidateName: "Jane Doe, RN",
  role: "Registered Nurse",
  specialty: "Med-Surg",
  facilityName: "Memorial Hermann TMC",
  facilityCity: "Houston",
  facilityState: "TX",
  facilityZip: "77002",
  contractMonth: "January",
  fiscalYear: "2026",
  contractWeeks: "13",
  guaranteedHours: "36",
  travelerQualified: "yes",
  taxHomeConfirmed: "yes",
  regularBillRate: "75",
  overtimeBillRate: "112.5",
  holidayBillRate: "150",
  vmsFeePct: "3",
  orientationHours: "8",
  orientationBillable: "yes",
  w2TaxableRate: "25",
  weeklyLodgingStipend: "770",
  weeklyMieStipend: "476",
  autoFillFromGsa: "no",
  manualOverride: "yes",
  overtimePayRate: "37.5",
  holidayPayRate: "50",
  payrollTaxPct: "10",
  workersCompPct: "3",
  adpMode: "approx",
  adpActualFee: "",
  adpApproxTier: "8",
  adpApproxCustom: "",
  adpFrequency: "per_week",
};

const EMPTY_DATA = Object.fromEntries(
  Object.keys(SAMPLE_DATA).map((k) => [k, k.endsWith("Qualified") || k.endsWith("Confirmed") || k === "autoFillFromGsa" || k === "manualOverride" || k === "orientationBillable" ? "no" : ""]),
);
EMPTY_DATA.contractWeeks = "13";
EMPTY_DATA.guaranteedHours = "36";
EMPTY_DATA.adpMode = "approx";
EMPTY_DATA.adpApproxTier = "8";
EMPTY_DATA.adpFrequency = "per_week";

const STORAGE_KEY = "trital_ratelab_v2";

/* ===================================================================== */
/*  Risk classification                                                  */
/* ===================================================================== */

function classifyMargin(weeklyProfit, marginPct) {
  if (weeklyProfit < 0) return { label: "Critical — negative profit", tone: "critical" };
  if (marginPct < 0.15) return { label: "High Risk", tone: "red" };
  if (marginPct < 0.20) return { label: "Review", tone: "yellow" };
  return { label: "Healthy", tone: "green" };
}

const TONE_STYLES = {
  green:    { bg: "bg-emerald-50", border: "border-emerald-200", pill: "bg-emerald-100 text-emerald-800 border-emerald-200", text: "text-emerald-700" },
  yellow:   { bg: "bg-amber-50",   border: "border-amber-200",   pill: "bg-amber-100 text-amber-800 border-amber-200",       text: "text-amber-700" },
  red:      { bg: "bg-rose-50",    border: "border-rose-200",    pill: "bg-rose-100 text-rose-800 border-rose-200",          text: "text-rose-700" },
  critical: { bg: "bg-rose-100",   border: "border-rose-300",    pill: "bg-rose-600 text-white border-rose-700",             text: "text-rose-800" },
};

/* ===================================================================== */
/*  Field components                                                     */
/* ===================================================================== */

function Field({ label, helper, children, span = 1 }) {
  const colSpan = span === 2 ? "sm:col-span-2" : "";
  return (
    <div className={colSpan}>
      <label className="block text-[12.5px] font-semibold text-slate-700 mb-1">{label}</label>
      {children}
      {helper && <p className="mt-1 text-[11px] text-slate-500 leading-snug">{helper}</p>}
    </div>
  );
}

function NumInput({ value, onChange, placeholder, testId, step = "0.01", min, max }) {
  return (
    <input
      type="number"
      inputMode="decimal"
      step={step}
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] tabular-nums focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
      data-testid={testId}
    />
  );
}

function TextInput({ value, onChange, placeholder, testId }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
      data-testid={testId}
    />
  );
}

function YesNo({ value, onChange, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
      data-testid={testId}
    >
      <option value="">— select —</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  );
}

function Section({ title, subtitle, children, testId }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-7" data-testid={testId}>
      <h2 className="font-display font-bold text-lg text-trital-navy">{title}</h2>
      {subtitle && <p className="mt-1 text-[13px] text-slate-500">{subtitle}</p>}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">{children}</div>
    </section>
  );
}

function Stat({ label, value, tone, testId }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-4">
      <div className="text-[10.5px] uppercase tracking-wider text-white/65 leading-tight">{label}</div>
      <div
        className={`mt-1 font-display font-black text-2xl tabular-nums ${tone === "critical" ? "text-rose-300" : tone === "red" ? "text-rose-200" : tone === "yellow" ? "text-amber-200" : "text-white"}`}
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  );
}

function ResultsRow({ label, value, accent = false, testId }) {
  return (
    <div className={`flex items-center justify-between py-2 ${accent ? "border-t border-slate-200 mt-2 pt-3" : ""}`}>
      <span className={`text-[13px] ${accent ? "font-semibold text-trital-navy" : "text-slate-700"}`}>{label}</span>
      <span className={`tabular-nums font-semibold ${accent ? "text-trital-navy text-base" : "text-slate-900 text-[13.5px]"}`} data-testid={testId}>
        {value}
      </span>
    </div>
  );
}

/* ===================================================================== */
/*  Main component                                                       */
/* ===================================================================== */

export default function RecruiterOsRateLab() {
  // Load saved state (or sample data on first visit).
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...EMPTY_DATA, ...JSON.parse(raw) };
    } catch {}
    return { ...EMPTY_DATA };
  });
  const [gsaResult, setGsaResult] = useState(null);
  const [gsaLoading, setGsaLoading] = useState(false);
  // Track origin of current stipend values: "manual" | "gsa".
  // Drives the "Auto-filled from GSA rate — editable" helper text and
  // resets to "manual" the moment the user types in either stipend field.
  const [stipendSource, setStipendSource] = useState("manual");
  // Holds GSA-derived weekly stipends when Manual Override is ON and we
  // refuse to silently overwrite the recruiter's values. Surfaces the
  // "Apply GSA Rates" banner inside Section 4.
  const [pendingGsaRates, setPendingGsaRates] = useState(null);
  const [savedCount, setSavedCount] = useState(0);
  const [shareUrl, setShareUrl] = useState("");
  // Send Pay Package dialog state
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ to_email: "", to_name: "", cc: "", message: "" });
  const [sending, setSending] = useState(false);
  const printRef = useRef(null);

  React.useEffect(() => {
    document.title = "RateLab™ Travel W2 Calculator | TRITAL Recruiter OS™";
  }, []);

  // Persist on every change (debounced via useEffect).
  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [data]);

  function update(key, value) {
    // Any user edit to either stipend field flips the origin back to
    // "manual" so the helper text disappears / updates.
    if (key === "weeklyLodgingStipend" || key === "weeklyMieStipend") {
      setStipendSource("manual");
    }
    setData((prev) => ({ ...prev, [key]: value }));
  }

  /** Apply pending GSA rates from the amber banner — recruiter opted in. */
  function applyGsaRates() {
    const p = pendingGsaRates;
    if (!p) return;
    setData((prev) => ({
      ...prev,
      autoFillFromGsa: "yes",
      weeklyLodgingStipend: String(p.lodgingWeekly),
      weeklyMieStipend: String(p.mieWeekly),
    }));
    setStipendSource("gsa");
    setPendingGsaRates(null);
    toast.success(`GSA rates applied: lodging $${p.lodgingWeekly}/wk, M&IE $${p.mieWeekly}/wk`);
  }

  /* ---------- GSA Lookup ---------- */
  async function lookupGsa() {
    if (!data.fiscalYear) {
      toast.error("Enter a fiscal year first.");
      return;
    }
    setGsaLoading(true);
    try {
      const res = await fetch(`/api/gsa/perdiem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          city: data.facilityCity,
          state: data.facilityState,
          zip: data.facilityZip,
          fiscalYear: data.fiscalYear,
          month: data.contractMonth,
        }),
      });
      // The endpoint MUST return JSON. If the response isn't OK or isn't
      // JSON (e.g. Vercel 404 HTML), fall through to the catch.
      const ct = res.headers.get("content-type") || "";
      if (!res.ok || !ct.includes("application/json")) {
        const snippet = (await res.text()).slice(0, 120);
        // eslint-disable-next-line no-console
        console.warn(`[ratelab] GSA endpoint non-JSON (${res.status}): ${snippet}`);
        throw new Error(`gsa_endpoint_unreachable_${res.status}`);
      }
      const out = await res.json();
      setGsaResult(out);
      if (out.apiStatus === "success") {
        toast.success(`GSA found (${out.sourceUsed}): $${out.weeklyMaxStipend}/wk max`);
      } else if (out.apiStatus === "fallback") {
        toast.warning(out.warning || "Used GSA fallback rate.");
      } else {
        toast.error(out.warning || "Unable to reach GSA rate service. Please enter stipends manually.");
      }
      // Auto-fill stipends from GSA whenever the API returns real numbers.
      // Behavior:
      //   • Manual Override OFF                     → auto-apply (always)
      //   • Manual Override ON + empty/zero stipends → auto-apply (nothing to overwrite)
      //   • Manual Override ON + non-zero stipends   → stash as pending,
      //     surface the amber "Apply GSA Rates" banner in Section 4.
      const okStatus = out.apiStatus === "success" || out.apiStatus === "fallback";
      if (okStatus && out.weeklyMaxStipend > 0) {
        const lodgingWeekly = Math.round((out.lodgingDaily || 0) * 7 * 100) / 100;
        const mieWeekly     = Math.round((out.mieDaily     || 0) * 7 * 100) / 100;

        const currentLodging = parseFloat(data.weeklyLodgingStipend || 0) || 0;
        const currentMie     = parseFloat(data.weeklyMieStipend     || 0) || 0;
        const hasExistingValues = currentLodging > 0 || currentMie > 0;
        const manualOverrideOn  = data.manualOverride === "yes";

        if (manualOverrideOn && hasExistingValues) {
          setPendingGsaRates({ lodgingWeekly, mieWeekly });
          toast.warning("Manual override is ON — GSA rates fetched but not applied.");
        } else {
          setData((prev) => ({
            ...prev,
            autoFillFromGsa: "yes",
            weeklyLodgingStipend: String(lodgingWeekly),
            weeklyMieStipend: String(mieWeekly),
          }));
          setStipendSource("gsa");
          setPendingGsaRates(null);
        }
      } else {
        setPendingGsaRates(null);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[ratelab] GSA lookup failed:", err && err.message);
      toast.error("Unable to reach GSA rate service. Please enter stipends manually.");
      setGsaResult({
        sourceUsed: "Manual",
        apiStatus: "failed",
        warning: "Unable to reach GSA rate service. Please enter stipends manually.",
        lodgingDaily: 0, mieDaily: 0, dailyTotal: 0, weeklyMaxStipend: 0,
      });
    } finally {
      setGsaLoading(false);
    }
  }

  /* ---------- Calculations ---------- */
  const calc = useMemo(() => {
    const hours = num(data.guaranteedHours);
    const weeks = num(data.contractWeeks);
    const billRate = num(data.regularBillRate);
    const vmsPct = num(data.vmsFeePct) / 100;
    const w2 = num(data.w2TaxableRate);
    const lodge = num(data.weeklyLodgingStipend);
    const mie = num(data.weeklyMieStipend);
    const taxPct = num(data.payrollTaxPct) / 100;
    const wcPct = num(data.workersCompPct) / 100;

    let adpWeekly = 0;
    if (data.adpMode === "actual") {
      adpWeekly = num(data.adpActualFee);
    } else {
      const tier = data.adpApproxTier === "custom" ? num(data.adpApproxCustom) : num(data.adpApproxTier);
      adpWeekly = tier;
    }
    if (data.adpFrequency === "per_paycheck") {
      adpWeekly = adpWeekly; // 1 weekly paycheck = same as per week
    } else if (data.adpFrequency === "per_assignment") {
      adpWeekly = weeks > 0 ? adpWeekly / weeks : 0;
    }

    const weeklyBillRevenue = billRate * hours;
    const vmsFee = weeklyBillRevenue * vmsPct;
    const netRevenue = weeklyBillRevenue - vmsFee;
    const taxableW2 = w2 * hours;
    const stipend = lodge + mie;
    const candidateWeeklyGross = taxableW2 + stipend;
    const blendedRate = hours > 0 ? candidateWeeklyGross / hours : NaN;
    const payrollTaxCost = taxableW2 * taxPct;
    const wcCost = taxableW2 * wcPct;
    const totalWeeklyCost = taxableW2 + stipend + payrollTaxCost + wcCost + adpWeekly;
    const weeklyProfit = netRevenue - totalWeeklyCost;
    const profitPerHour = hours > 0 ? weeklyProfit / hours : NaN;
    const marginPct = netRevenue > 0 ? weeklyProfit / netRevenue : 0;
    const contractRevenue = weeklyBillRevenue * weeks;
    const contractProfit = weeklyProfit * weeks;

    const risk = classifyMargin(weeklyProfit, marginPct);

    return {
      hours, weeks, weeklyBillRevenue, vmsFee, netRevenue,
      taxableW2, stipend, candidateWeeklyGross, blendedRate,
      payrollTaxCost, wcCost, adpWeekly, totalWeeklyCost, weeklyProfit, profitPerHour,
      marginPct, contractRevenue, contractProfit, risk,
    };
  }, [data]);

  /* ---------- Compliance flags ---------- */
  const stipendOverGsa = gsaResult?.weeklyMaxStipend > 0 && calc.stipend > gsaResult.weeklyMaxStipend;
  const travelerWarning = data.travelerQualified === "no" && calc.stipend > 0;
  const taxHomeWarning = data.taxHomeConfirmed === "no" && calc.stipend > 0;
  const gsaCompliance = !gsaResult
    ? { tone: "yellow", label: "GSA not yet looked up" }
    : gsaResult.apiStatus === "failed"
      ? { tone: "yellow", label: "Manual — GSA unavailable" }
      : stipendOverGsa
        ? { tone: "red", label: "Stipend exceeds GSA weekly max" }
        : { tone: "green", label: "Within GSA weekly max" };

  /* ---------- Actions ---------- */
  function loadSample() {
    setData({ ...SAMPLE_DATA });
    setGsaResult(null);
    toast.success("Sample data loaded.");
  }
  function resetAll() {
    setData({ ...EMPTY_DATA });
    setGsaResult(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    toast.success("Calculator reset.");
  }
  function saveCalc() {
    try {
      const key = `trital_ratelab_save_${Date.now()}`;
      localStorage.setItem(key, JSON.stringify({ data, gsaResult, savedAt: new Date().toISOString() }));
      setSavedCount((c) => c + 1);
      toast.success("Calculation saved locally.");
    } catch {
      toast.error("Save failed (storage full).");
    }
  }

  async function saveToWorkspace() {
    try {
      const res = await fetch(`${API}/ratelab-calculations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          inputs: data,
          results: {
            weeklyBillRevenue: calc.weeklyBillRevenue,
            netRevenue: calc.netRevenue,
            taxableW2: calc.taxableW2,
            stipend: calc.stipend,
            candidateWeeklyGross: calc.candidateWeeklyGross,
            blendedRate: calc.blendedRate,
            totalWeeklyCost: calc.totalWeeklyCost,
            weeklyProfit: calc.weeklyProfit,
            marginPct: calc.marginPct,
            contractRevenue: calc.contractRevenue,
            contractProfit: calc.contractProfit,
          },
          gsa: gsaResult || {},
          candidate_name: data.candidateName,
          role: data.role,
          facility: data.facilityName,
          facility_state: data.facilityState,
          label: `${data.candidateName || "Unnamed"} — ${data.facilityCity || ""} ${data.facilityState || ""}`.trim(),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const out = await res.json();
      if (!out.share_id) throw new Error("no share id");
      const shareUrl = `${window.location.origin}/ratelab/share/${out.share_id}`;
      try { await navigator.clipboard.writeText(shareUrl); } catch {}
      toast.success("Saved to workspace — share link copied!");
      setShareUrl(shareUrl);
    } catch {
      toast.error("Workspace save failed.");
    }
  }

  async function downloadPayPackagePdf() {
    try {
      const res = await fetch(`${API}/ratelab/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // export_mode is informational on this public route — server always
        // returns the candidate-safe PDF regardless of value.
        body: JSON.stringify({ inputs: data, gsa: gsaResult || {}, export_mode: "candidate" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = (data.candidateName || "candidate").replace(/\s+/g, "_");
      a.download = `TRITAL_PayPackage_${name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Candidate Pay Package PDF downloaded.");
    } catch {
      toast.error("PDF download failed. Check connection.");
    }
  }

  async function downloadInternalAgencyPdf() {
    // Internal P&L PDF requires recruiter / admin / compliance auth.
    const token = (() => {
      try { return localStorage.getItem("trital_token") || ""; } catch { return ""; }
    })();
    if (!token) {
      toast.error("Sign in as recruiter or admin to download the Internal Agency PDF.");
      return;
    }
    try {
      const res = await fetch(`${API}/ratelab/pdf-internal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ inputs: data, gsa: gsaResult || {} }),
      });
      if (res.status === 401 || res.status === 403) {
        toast.error("Internal Agency PDF is restricted to recruiter accounts.");
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = (data.candidateName || "candidate").replace(/\s+/g, "_");
      a.download = `TRITAL_InternalAgency_${name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Internal Agency PDF downloaded.");
    } catch {
      toast.error("Internal PDF download failed. Check connection.");
    }
  }

  function openSendDialog() {
    setSendForm((f) => ({
      ...f,
      to_name: data.candidateName || "",
      to_email: f.to_email || "",
    }));
    setSendOpen(true);
  }

  async function sendPayPackage(e) {
    e?.preventDefault?.();
    const to_email = sendForm.to_email.trim();
    if (!to_email) {
      toast.error("Add the candidate's email.");
      return;
    }
    setSending(true);
    try {
      const cc = sendForm.cc
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 3);
      const res = await fetch(`${API}/ratelab/send-pay-package`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to_email,
          to_name: sendForm.to_name,
          cc,
          message: sendForm.message,
          inputs: data,
          gsa: gsaResult || {},
        }),
      });
      const out = await res.json();
      if (res.ok && out.ok && out.delivered) {
        toast.success(`Pay Package emailed to ${to_email} (${out.provider}).`);
        setSendOpen(false);
        setSendForm({ to_email: "", to_name: "", cc: "", message: "" });
      } else {
        toast.error(out.error || "Email send failed. Try CSV/PDF download as fallback.");
      }
    } catch {
      toast.error("Network error. Email not sent.");
    } finally {
      setSending(false);
    }
  }
  function duplicateCalc() {
    update("candidateName", (data.candidateName || "") + " (copy)");
    toast.success("Duplicated — edit the copy below.");
  }
  function exportCsv() {
    const rows = [
      ["Section", "Field", "Value"],
      ...Object.entries(data).map(([k, v]) => ["Inputs", k, v]),
      ["Results", "Weekly Bill Revenue", calc.weeklyBillRevenue.toFixed(2)],
      ["Results", "VMS Fee", calc.vmsFee.toFixed(2)],
      ["Results", "Net Revenue", calc.netRevenue.toFixed(2)],
      ["Results", "Taxable W2 Wages", calc.taxableW2.toFixed(2)],
      ["Results", "Non-Taxable Stipend", calc.stipend.toFixed(2)],
      ["Results", "Candidate Weekly Gross", calc.candidateWeeklyGross.toFixed(2)],
      ["Results", "Blended Rate", isFinite(calc.blendedRate) ? calc.blendedRate.toFixed(2) : "N/A"],
      ["Results", "Payroll Tax Cost", calc.payrollTaxCost.toFixed(2)],
      ["Results", "Workers Comp Cost", calc.wcCost.toFixed(2)],
      ["Results", "ADP Weekly Cost", calc.adpWeekly.toFixed(2)],
      ["Results", "Total Weekly Agency Cost", calc.totalWeeklyCost.toFixed(2)],
      ["Results", "Weekly Profit", calc.weeklyProfit.toFixed(2)],
      ["Results", "Profit Per Hour", isFinite(calc.profitPerHour) ? calc.profitPerHour.toFixed(2) : "N/A"],
      ["Results", "Margin %", (calc.marginPct * 100).toFixed(2)],
      ["Results", "Contract Revenue", calc.contractRevenue.toFixed(2)],
      ["Results", "Contract Profit", calc.contractProfit.toFixed(2)],
      ["GSA", "Source Used", gsaResult?.sourceUsed || "Manual"],
      ["GSA", "Lodging Daily", gsaResult?.lodgingDaily || ""],
      ["GSA", "M&IE Daily", gsaResult?.mieDaily || ""],
      ["GSA", "Weekly Max Stipend", gsaResult?.weeklyMaxStipend || ""],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ratelab_${(data.candidateName || "calc").replace(/\s+/g, "_")}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  }
  function printManager() {
    window.print();
  }
  function printCandidate() {
    document.body.classList.add("ratelab-print-candidate");
    window.print();
    setTimeout(() => document.body.classList.remove("ratelab-print-candidate"), 500);
  }

  /* ---------- Render ---------- */
  const tone = TONE_STYLES[calc.risk.tone];

  return (
    <WorkforceOsLayout
      eyebrow="TRITAL Recruiter OS™"
      breadcrumb="RateLab Travel W2 Calculator"
      title="RateLab™ Travel W2 Calculator"
      description="Production-grade pay package, GSA stipend, and margin calculator for healthcare staffing teams. Live results, GSA compliance, internal manager + candidate-safe exports."
    >
      {/* ============ Sticky top summary ============ */}
      <div
        className="sticky top-[84px] z-30 bg-trital-navy text-white shadow-[0_8px_20px_-12px_rgba(0,0,0,0.4)]"
        data-testid="ratelab-sticky-summary"
      >
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Candidate Weekly Gross" value={fmt(calc.candidateWeeklyGross)} testId="rl-stat-gross" />
            <Stat label="Blended Rate" value={isFinite(calc.blendedRate) ? `${fmt(calc.blendedRate)}/hr` : "N/A"} testId="rl-stat-blended" />
            <Stat label="Weekly Profit" value={fmt(calc.weeklyProfit)} tone={calc.weeklyProfit < 0 ? "critical" : null} testId="rl-stat-profit" />
            <Stat label="Margin %" value={fmtPct(calc.marginPct)} tone={calc.risk.tone === "red" || calc.risk.tone === "critical" ? "red" : calc.risk.tone === "yellow" ? "yellow" : null} testId="rl-stat-margin" />
            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              <div className="text-[10.5px] uppercase tracking-wider text-white/65">Risk</div>
              <span
                className={`mt-1 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11.5px] font-bold border ${tone.pill}`}
                data-testid="rl-stat-risk"
              >
                {calc.risk.tone === "green" ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                {calc.risk.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6" ref={printRef}>
        {/* ============ Left: forms ============ */}
        <div className="space-y-5">
          {/* Action bar */}
          <div className="flex flex-wrap items-center gap-2" data-testid="ratelab-actions">
            <button type="button" onClick={loadSample} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold" data-testid="ratelab-sample-btn">
              <Sparkles className="w-3.5 h-3.5" /> Use Sample Data
            </button>
            <button type="button" onClick={resetAll} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-[13px] text-slate-700" data-testid="ratelab-reset-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Reset
            </button>
            <button type="button" onClick={downloadPayPackagePdf} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-red text-white text-[13px] font-semibold" data-testid="ratelab-pdf-btn">
              <FileDown className="w-3.5 h-3.5" /> Download Candidate Pay Package PDF
            </button>
            <button type="button" onClick={downloadInternalAgencyPdf} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold" data-testid="ratelab-internal-pdf-btn" title="Recruiter / admin only — full agency P&L with margin and profit.">
              <FileDown className="w-3.5 h-3.5" /> Download Internal Agency PDF
            </button>
            <button type="button" onClick={openSendDialog} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold" data-testid="ratelab-send-btn">
              <Send className="w-3.5 h-3.5" /> Send to Candidate
            </button>
            <button type="button" onClick={saveToWorkspace} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-medical-blue-soft border border-trital-navy text-trital-navy text-[13px] font-semibold" data-testid="ratelab-save-workspace-btn">
              <Save className="w-3.5 h-3.5" /> Save to Workspace
            </button>
            <button type="button" onClick={saveCalc} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-[13px] text-slate-700" data-testid="ratelab-save-btn">
              <Save className="w-3.5 h-3.5" /> Save Local
            </button>
            <button type="button" onClick={duplicateCalc} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-[13px] text-slate-700" data-testid="ratelab-duplicate-btn">
              <Copy className="w-3.5 h-3.5" /> Duplicate
            </button>
            <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-[13px] text-slate-700" data-testid="ratelab-csv-btn">
              <FileDown className="w-3.5 h-3.5" /> CSV
            </button>
            <button type="button" onClick={printManager} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-red text-white text-[13px] font-semibold" data-testid="ratelab-print-manager-btn">
              <Printer className="w-3.5 h-3.5" /> Manager Profit Sheet
            </button>
            <button type="button" onClick={printCandidate} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-trital-navy text-trital-navy text-[13px] font-semibold" data-testid="ratelab-print-candidate-btn">
              <FileText className="w-3.5 h-3.5" /> Candidate Deal Sheet
            </button>
            {savedCount > 0 && <span className="text-[11.5px] text-slate-500">Saved snapshots: {savedCount}</span>}
          </div>

          {/* Share URL banner — shows after Save to Workspace */}
          {shareUrl && (
            <div
              className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex flex-wrap items-center gap-3"
              data-testid="ratelab-share-banner"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-semibold text-emerald-900">Shareable link copied to clipboard</div>
                <code
                  className="block text-[11.5px] text-emerald-800 mt-0.5 truncate"
                  data-testid="ratelab-share-url"
                >
                  {shareUrl}
                </code>
              </div>
              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-trital-navy text-white text-[12px] font-semibold"
                data-testid="ratelab-share-open-btn"
              >
                Open <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}

          {/* 1. Assignment Details */}
          <Section title="1. Assignment Details" subtitle="Candidate, role, and contract setup." testId="rl-section-1">
            <Field label="Candidate Name" helper="Used for deal sheet export only."><TextInput value={data.candidateName} onChange={(v) => update("candidateName", v)} placeholder="Jane Doe, RN" testId="rl-candidate-name" /></Field>
            <Field label="Role / Job Title"><TextInput value={data.role} onChange={(v) => update("role", v)} placeholder="Registered Nurse" testId="rl-role" /></Field>
            <Field label="Specialty"><TextInput value={data.specialty} onChange={(v) => update("specialty", v)} placeholder="Med-Surg" testId="rl-specialty" /></Field>
            <Field label="Facility / Client Name"><TextInput value={data.facilityName} onChange={(v) => update("facilityName", v)} placeholder="Memorial Hermann TMC" testId="rl-facility-name" /></Field>
            <Field label="Facility City"><TextInput value={data.facilityCity} onChange={(v) => update("facilityCity", v)} placeholder="Houston" testId="rl-facility-city" /></Field>
            <Field label="Facility State (2-letter)"><TextInput value={data.facilityState} onChange={(v) => update("facilityState", v.toUpperCase().slice(0, 2))} placeholder="TX" testId="rl-facility-state" /></Field>
            <Field label="Facility ZIP" helper="Used for best GSA lookup accuracy."><TextInput value={data.facilityZip} onChange={(v) => update("facilityZip", v.replace(/\D/g, "").slice(0, 5))} placeholder="77002" testId="rl-facility-zip" /></Field>
            <Field label="Contract Start Month">
              <select value={data.contractMonth} onChange={(e) => update("contractMonth", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] bg-white" data-testid="rl-contract-month">
                <option value="">— select —</option>
                {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Fiscal Year" helper="GSA per diem rates are fiscal-year based."><TextInput value={data.fiscalYear} onChange={(v) => update("fiscalYear", v.replace(/\D/g, "").slice(0, 4))} placeholder="2026" testId="rl-fiscal-year" /></Field>
            <Field label="Contract Length (weeks)"><NumInput value={data.contractWeeks} onChange={(v) => update("contractWeeks", v)} placeholder="13" testId="rl-contract-weeks" step="1" /></Field>
            <Field label="Guaranteed Hours / Week" helper="Usually 36, 40, or 48."><NumInput value={data.guaranteedHours} onChange={(v) => update("guaranteedHours", v)} placeholder="36" testId="rl-guaranteed-hours" step="1" /></Field>
            <Field label="Traveler Qualified?" helper="Determines whether stipend treatment is appropriate."><YesNo value={data.travelerQualified} onChange={(v) => update("travelerQualified", v)} testId="rl-traveler-qualified" /></Field>
            <Field label="Tax Home Confirmed?" helper="Required for non-taxable stipend treatment."><YesNo value={data.taxHomeConfirmed} onChange={(v) => update("taxHomeConfirmed", v)} testId="rl-tax-home" /></Field>
          </Section>

          {/* 2. GSA Per Diem Lookup */}
          <Section title="2. GSA Per Diem Lookup" subtitle="ZIP → City+State → State → CONUS — backend proxy." testId="rl-section-2">
            <div className="sm:col-span-2 flex flex-wrap gap-2">
              <button type="button" onClick={lookupGsa} disabled={gsaLoading} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold disabled:opacity-50" data-testid="rl-gsa-lookup-btn">
                <MapPin className="w-3.5 h-3.5" /> {gsaLoading ? "Looking up…" : "Look up GSA per diem"}
              </button>
            </div>
            {gsaResult && (
              <div className="sm:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rl-gsa-result">
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="overline text-slate-500 text-[10px]">Source</div>
                  <div className="font-bold text-trital-navy" data-testid="rl-gsa-source">{gsaResult.sourceUsed}</div>
                </div>
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="overline text-slate-500 text-[10px]">Lodging / day</div>
                  <div className="font-bold tabular-nums" data-testid="rl-gsa-lodging">{fmt(gsaResult.lodgingDaily)}</div>
                </div>
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="overline text-slate-500 text-[10px]">M&amp;IE / day</div>
                  <div className="font-bold tabular-nums" data-testid="rl-gsa-mie">{fmt(gsaResult.mieDaily)}</div>
                </div>
                <div className="rounded-xl border border-trital-navy bg-trital-medical-blue-soft p-3">
                  <div className="overline text-slate-500 text-[10px]">Weekly Max Stipend</div>
                  <div className="font-bold text-trital-navy tabular-nums" data-testid="rl-gsa-weekly-max">{fmt(gsaResult.weeklyMaxStipend)}</div>
                </div>
                {gsaResult.warning && (
                  <div className="sm:col-span-2 md:col-span-4 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{gsaResult.warning}</span>
                  </div>
                )}
              </div>
            )}
            <Field label="Auto-fill stipend from GSA?" helper="If yes, lodging + M&IE auto-populate when you click Look up.">
              <YesNo value={data.autoFillFromGsa} onChange={(v) => update("autoFillFromGsa", v)} testId="rl-autofill" />
            </Field>
            <Field label="Manual stipend override?" helper="Allow manual entry above. Shows warning if above GSA.">
              <YesNo value={data.manualOverride} onChange={(v) => update("manualOverride", v)} testId="rl-manual-override" />
            </Field>
          </Section>

          {/* 3. Bill Rate & VMS/MSP Fee */}
          <Section title="3. Bill Rate & VMS/MSP Fee" subtitle="Revenue side of the assignment." testId="rl-section-3">
            <Field label="Regular Bill Rate ($/hr)" helper="Client bill rate per regular hour."><NumInput value={data.regularBillRate} onChange={(v) => update("regularBillRate", v)} testId="rl-bill-rate" /></Field>
            <Field label="Overtime Bill Rate ($/hr)"><NumInput value={data.overtimeBillRate} onChange={(v) => update("overtimeBillRate", v)} testId="rl-ot-bill-rate" /></Field>
            <Field label="Holiday Bill Rate ($/hr)"><NumInput value={data.holidayBillRate} onChange={(v) => update("holidayBillRate", v)} testId="rl-holiday-bill-rate" /></Field>
            <Field label="VMS/MSP Fee %" helper="Fee deducted from bill revenue."><NumInput value={data.vmsFeePct} onChange={(v) => update("vmsFeePct", v)} testId="rl-vms-fee" /></Field>
            <Field label="Orientation Hours" helper="Add if orientation is separately billed/paid."><NumInput value={data.orientationHours} onChange={(v) => update("orientationHours", v)} testId="rl-orientation-hours" step="1" /></Field>
            <Field label="Orientation Billable?" helper="If no, orientation may reduce margin.">
              <YesNo value={data.orientationBillable} onChange={(v) => update("orientationBillable", v)} testId="rl-orientation-billable" />
            </Field>
          </Section>

          {/* 4. Candidate Pay Package */}
          <Section title="4. Candidate Pay Package" subtitle="What the clinician takes home each week." testId="rl-section-4">
            <Field label="W2 Taxable Hourly Rate ($/hr)"><NumInput value={data.w2TaxableRate} onChange={(v) => update("w2TaxableRate", v)} testId="rl-w2-rate" /></Field>
            <Field
              label="Weekly Lodging Stipend ($)"
              helper={stipendSource === "gsa" ? "Auto-filled from GSA rate — editable" : null}
            >
              <NumInput value={data.weeklyLodgingStipend} onChange={(v) => update("weeklyLodgingStipend", v)} testId="rl-lodging-stipend" />
            </Field>
            <Field
              label="Weekly M&IE Stipend ($)"
              helper={stipendSource === "gsa" ? "Auto-filled from GSA rate — editable" : null}
            >
              <NumInput value={data.weeklyMieStipend} onChange={(v) => update("weeklyMieStipend", v)} testId="rl-mie-stipend" />
            </Field>
            <Field label="Overtime Pay Rate ($/hr)"><NumInput value={data.overtimePayRate} onChange={(v) => update("overtimePayRate", v)} testId="rl-ot-pay-rate" /></Field>
            <Field label="Holiday Pay Rate ($/hr)"><NumInput value={data.holidayPayRate} onChange={(v) => update("holidayPayRate", v)} testId="rl-holiday-pay-rate" /></Field>

            {/* Pending GSA rates banner (Manual Override ON + existing values) */}
            {pendingGsaRates && (
              <div
                className="sm:col-span-2 text-[12.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2"
                data-testid="rl-pending-gsa-banner"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
                <div className="flex-1">
                  <div><b>Manual override is ON</b> — GSA rates were fetched but not applied.</div>
                  <div className="mt-0.5 text-amber-800">
                    Lodging {fmt(pendingGsaRates.lodgingWeekly)}/wk · M&amp;IE {fmt(pendingGsaRates.mieWeekly)}/wk
                  </div>
                </div>
                <button
                  type="button"
                  onClick={applyGsaRates}
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-trital-navy text-white text-[12px] font-semibold"
                  data-testid="rl-apply-gsa-rates"
                >
                  Apply GSA Rates
                </button>
              </div>
            )}

            {/* Inline warnings */}
            {(travelerWarning || taxHomeWarning || stipendOverGsa) && (
              <div className="sm:col-span-2 space-y-2" data-testid="rl-pay-warnings">
                {travelerWarning && (
                  <div className="text-[12.5px] text-rose-800 bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-start gap-2" data-testid="rl-warn-traveler">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span><b>Traveler not qualified</b> — stipend may need to be treated as taxable W2 wages.</span>
                  </div>
                )}
                {taxHomeWarning && (
                  <div className="text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2" data-testid="rl-warn-tax-home">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span><b>Tax home not confirmed</b> — non-taxable stipend eligibility is not confirmed.</span>
                  </div>
                )}
                {stipendOverGsa && (
                  <div className="text-[12.5px] text-rose-800 bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-start gap-2" data-testid="rl-warn-gsa-over">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span><b>Stipend exceeds GSA weekly max</b> — entered {fmt(calc.stipend)} vs GSA {fmt(gsaResult.weeklyMaxStipend)}.</span>
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* 5. Employer Burden & ADP/Payroll Cost */}
          <Section title="5. Employer Burden & ADP/Payroll Cost" subtitle="Internal cost side." testId="rl-section-5">
            <Field label="Employer Payroll Tax %" helper="Applies only to taxable W2 wages."><NumInput value={data.payrollTaxPct} onChange={(v) => update("payrollTaxPct", v)} testId="rl-payroll-tax" /></Field>
            <Field label="Workers Comp %" helper="Applies only to taxable W2 wages."><NumInput value={data.workersCompPct} onChange={(v) => update("workersCompPct", v)} testId="rl-workers-comp" /></Field>
            <Field label="ADP / Payroll Cost Mode">
              <select value={data.adpMode} onChange={(e) => update("adpMode", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] bg-white" data-testid="rl-adp-mode">
                <option value="actual">Use Actual ADP Fee</option>
                <option value="approx">Use Approximate ADP Fee</option>
              </select>
            </Field>
            <Field label="ADP Frequency">
              <select value={data.adpFrequency} onChange={(e) => update("adpFrequency", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] bg-white" data-testid="rl-adp-frequency">
                <option value="per_week">Per weekly payroll</option>
                <option value="per_paycheck">Per paycheck</option>
                <option value="per_assignment">Per assignment (spread)</option>
              </select>
            </Field>
            {data.adpMode === "actual" ? (
              <Field label="Actual ADP Fee ($)" helper="Enter the exact fee for the chosen frequency." span={2}><NumInput value={data.adpActualFee} onChange={(v) => update("adpActualFee", v)} testId="rl-adp-actual" /></Field>
            ) : (
              <>
                <Field label="Approximate ADP Tier">
                  <select value={data.adpApproxTier} onChange={(e) => update("adpApproxTier", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] bg-white" data-testid="rl-adp-approx-tier">
                    <option value="5">Low — $5</option>
                    <option value="8">Standard — $8</option>
                    <option value="12">High — $12</option>
                    <option value="custom">Custom approximate…</option>
                  </select>
                </Field>
                {data.adpApproxTier === "custom" && (
                  <Field label="Custom Approximate ($)"><NumInput value={data.adpApproxCustom} onChange={(v) => update("adpApproxCustom", v)} testId="rl-adp-approx-custom" /></Field>
                )}
              </>
            )}
          </Section>

          {/* 8. Compliance disclaimer */}
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 text-[12.5px] text-slate-600 leading-relaxed" data-testid="rl-compliance-notes">
            <div className="flex items-center gap-2 text-trital-navy font-semibold">
              <ShieldCheck className="w-4 h-4" /> Compliance &amp; Disclaimer
            </div>
            <p className="mt-2">
              RateLab™ is an internal estimate tool for healthcare staffing pay-package and margin
              review. Results are estimates only and must be verified against client contracts,
              VMS/MSP rules, payroll setup, tax guidance, workers compensation classification,
              ADP/payroll records, applicable law, and final finance approval. GSA stipend lookup
              does not determine tax eligibility. Tax home and traveler qualification must be
              independently verified.
            </p>
          </div>
        </div>

        {/* ============ Right: Results & risk ============ */}
        <aside className="space-y-4">
          {/* Results card */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:sticky lg:top-[208px]" data-testid="rl-results-card">
            <div className="flex items-center gap-2">
              <Calculator className="w-4 h-4 text-trital-navy" />
              <h2 className="font-display font-bold text-lg text-trital-navy">Results &amp; Risk Summary</h2>
            </div>

            <ResultsRow label="Weekly Bill Revenue" value={fmt(calc.weeklyBillRevenue)} testId="rl-r-bill-rev" />
            <ResultsRow label="VMS/MSP Fee" value={fmt(calc.vmsFee)} testId="rl-r-vms" />
            <ResultsRow label="Net Revenue After VMS" value={fmt(calc.netRevenue)} accent testId="rl-r-net-rev" />

            <ResultsRow label="Weekly Taxable W2 Wages" value={fmt(calc.taxableW2)} testId="rl-r-taxable-w2" />
            <ResultsRow label="Weekly Non-Taxable Stipend" value={fmt(calc.stipend)} testId="rl-r-stipend" />
            <ResultsRow label="Candidate Weekly Gross" value={fmt(calc.candidateWeeklyGross)} accent testId="rl-r-cli-gross" />
            <ResultsRow label="Blended Hourly Rate" value={isFinite(calc.blendedRate) ? `${fmt(calc.blendedRate)}/hr` : "N/A"} testId="rl-r-blended" />

            <ResultsRow label="Payroll Tax Cost" value={fmt(calc.payrollTaxCost)} testId="rl-r-payroll-tax" />
            <ResultsRow label="Workers Comp Cost" value={fmt(calc.wcCost)} testId="rl-r-wc" />
            <ResultsRow label="ADP / Payroll Cost" value={fmt(calc.adpWeekly)} testId="rl-r-adp" />
            <ResultsRow label="Total Weekly Agency Cost" value={fmt(calc.totalWeeklyCost)} accent testId="rl-r-total-cost" />

            <ResultsRow label="Weekly Profit" value={fmt(calc.weeklyProfit)} accent testId="rl-r-weekly-profit" />
            <ResultsRow label="Profit Per Hour" value={isFinite(calc.profitPerHour) ? `${fmt(calc.profitPerHour)}/hr` : "N/A"} testId="rl-r-profit-per-hour" />
            <ResultsRow label="Margin %" value={fmtPct(calc.marginPct)} testId="rl-r-margin-pct" />
            <ResultsRow label="Total Contract Revenue" value={fmt(calc.contractRevenue)} testId="rl-r-contract-rev" />
            <ResultsRow label="Total Contract Profit" value={fmt(calc.contractProfit)} accent testId="rl-r-contract-profit" />

            <div className={`mt-4 rounded-xl border p-3 flex items-center gap-2 ${tone.bg} ${tone.border}`}>
              {calc.risk.tone === "green" ? <CheckCircle2 className={`w-4 h-4 ${tone.text}`} /> : <AlertTriangle className={`w-4 h-4 ${tone.text}`} />}
              <span className={`text-[12.5px] font-semibold ${tone.text}`} data-testid="rl-risk-label">{calc.risk.label}</span>
            </div>

            <div className={`mt-2 rounded-xl border p-3 flex items-center gap-2 ${TONE_STYLES[gsaCompliance.tone].bg} ${TONE_STYLES[gsaCompliance.tone].border}`}>
              <Info className={`w-4 h-4 ${TONE_STYLES[gsaCompliance.tone].text}`} />
              <span className={`text-[12.5px] font-semibold ${TONE_STYLES[gsaCompliance.tone].text}`} data-testid="rl-gsa-compliance">{gsaCompliance.label}</span>
            </div>
          </div>
        </aside>
      </div>

      {/* Print-only candidate deal sheet hides internal numbers */}
      <style>{`
        @media print {
          /* Hide everything that's not in the printable region by default for manager sheet, but use markers for candidate sheet */
          [data-testid="public-nav"], [data-testid="public-footer"], [data-testid="wos-breadcrumb"],
          [data-testid="wos-disclaimer"], [data-testid="ratelab-actions"], header, footer,
          [data-testid="rl-section-5"], [data-testid="rl-section-3"] {
            display: none !important;
          }
          .ratelab-print-candidate [data-testid="rl-section-1"] { display: block !important; }
          .ratelab-print-candidate [data-testid="rl-section-2"] { display: none !important; }
          .ratelab-print-candidate [data-testid="rl-section-4"] { display: block !important; }
          /* In candidate mode, hide internal numbers in the results card */
          .ratelab-print-candidate [data-testid="rl-r-bill-rev"],
          .ratelab-print-candidate [data-testid="rl-r-vms"],
          .ratelab-print-candidate [data-testid="rl-r-net-rev"],
          .ratelab-print-candidate [data-testid="rl-r-payroll-tax"],
          .ratelab-print-candidate [data-testid="rl-r-wc"],
          .ratelab-print-candidate [data-testid="rl-r-adp"],
          .ratelab-print-candidate [data-testid="rl-r-total-cost"],
          .ratelab-print-candidate [data-testid="rl-r-weekly-profit"],
          .ratelab-print-candidate [data-testid="rl-r-profit-per-hour"],
          .ratelab-print-candidate [data-testid="rl-r-margin-pct"],
          .ratelab-print-candidate [data-testid="rl-r-contract-profit"],
          .ratelab-print-candidate [data-testid="rl-risk-label"],
          .ratelab-print-candidate [data-testid="rl-gsa-compliance"] {
            display: none !important;
          }
        }
      `}</style>

      {/* Send Pay Package dialog */}
      {sendOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/45"
          role="dialog"
          aria-modal="true"
          data-testid="ratelab-send-dialog"
        >
          <form
            onSubmit={sendPayPackage}
            className="w-full max-w-[460px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 bg-trital-navy text-white">
              <div>
                <div className="font-display font-bold text-[16px]">Send Pay Package to Candidate</div>
                <div className="text-[11px] text-white/70">Emails the PDF via MS365 SMTP with Resend fallback.</div>
              </div>
              <button
                type="button"
                onClick={() => setSendOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/10"
                aria-label="Close"
                data-testid="ratelab-send-close-btn"
              >
                <X className="w-4 h-4" strokeWidth={1.8} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Candidate Email *</label>
                <input
                  type="email"
                  required
                  value={sendForm.to_email}
                  onChange={(e) => setSendForm({ ...sendForm, to_email: e.target.value })}
                  placeholder="jane@example.com"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
                  disabled={sending}
                  data-testid="ratelab-send-to-email"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Candidate Name (optional)</label>
                <input
                  type="text"
                  value={sendForm.to_name}
                  onChange={(e) => setSendForm({ ...sendForm, to_name: e.target.value })}
                  placeholder="Jane Doe, RN"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px]"
                  disabled={sending}
                  data-testid="ratelab-send-to-name"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">CC (recruiter or manager, comma-separated)</label>
                <input
                  type="text"
                  value={sendForm.cc}
                  onChange={(e) => setSendForm({ ...sendForm, cc: e.target.value })}
                  placeholder="recruiter@tritalcare.com"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px]"
                  disabled={sending}
                  data-testid="ratelab-send-cc"
                />
                <p className="mt-1 text-[10.5px] text-slate-500">Up to 3 CC addresses.</p>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Personal message (optional)</label>
                <textarea
                  value={sendForm.message}
                  onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                  placeholder="Hi Jane — here's the package we discussed. Looking forward to your thoughts!"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px]"
                  disabled={sending}
                  maxLength={2000}
                  data-testid="ratelab-send-message"
                />
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 flex items-start gap-2 text-[11.5px] text-slate-600">
                <Info className="w-3.5 h-3.5 mt-0.5 text-slate-500 shrink-0" />
                <span>
                  The candidate receives the safe Pay Package PDF (no internal margin/profit).
                  Send activity is logged for audit.
                </span>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 bg-slate-50 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setSendOpen(false)}
                className="px-4 py-2 rounded-full border border-slate-300 text-[12.5px] font-semibold text-slate-700"
                disabled={sending}
                data-testid="ratelab-send-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sending || !sendForm.to_email.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[12.5px] font-semibold disabled:opacity-50"
                data-testid="ratelab-send-submit-btn"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" /> Send Pay Package
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </WorkforceOsLayout>
  );
}
